import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { calendarService } from "@/services/calendarService";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// LocalStorage key for OAuth communication (bypasses COOP restrictions)
const OAUTH_RESULT_KEY = 'calendar_oauth_result';

const CalendarCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const state = searchParams.get('state'); // This contains userId or JSON {userId, source}

      // Check if this is a mobile OAuth callback (source=mobile in state)
      // If yes, redirect to deep link and return early (don't modify web flow)
      if (code && state) {
        try {
          const stateData = JSON.parse(state);
          if (stateData.source === 'mobile') {
            // Redirect to deep link for mobile app
            const deepLinkUrl = `step://calendar-callback?code=${code}${state ? `&state=${encodeURIComponent(state)}` : ''}`;
            window.location.href = deepLinkUrl;
            return;
          }
        } catch {
          // If state is not JSON (legacy: just userId string), continue with web flow
        }
      }

      // Helper to notify parent via localStorage (COOP-safe)
      const notifyViaStorage = (success: boolean, errorMsg?: string) => {
        try {
          localStorage.setItem(OAUTH_RESULT_KEY, JSON.stringify({
            success,
            error: errorMsg,
            timestamp: Date.now(),
          }));
        } catch {
          // localStorage not available, ignore
        }
      };

      // Helper to notify parent via postMessage (if COOP allows)
      const notifyViaPostMessage = () => {
        try {
          if (window.opener) {
            window.opener.postMessage(
              {
                type: 'calendar-oauth-callback',
                code,
                error,
                state,
              },
              window.location.origin
            );
            return true;
          }
        } catch {
          // COOP blocked access to window.opener
        }
        return false;
      };

      // Check if this is a popup that should communicate back
      const isPopup = window.opener !== null || window.history.length <= 2;

      if (isPopup) {
        if (error) {
          const errorMsg = error === 'access_denied' 
            ? 'You cancelled the calendar connection' 
            : `Failed to connect calendar: ${error}`;
          
          // Try postMessage first, then use localStorage as fallback
          notifyViaPostMessage();
          notifyViaStorage(false, errorMsg);
          
          // Try to close popup
          setTimeout(() => window.close(), 100);
          return;
        }

        if (code) {
          try {
            const redirectUri = `${window.location.origin}/calendar/callback`;
            await calendarService.connectCalendar('google', code, redirectUri);
            
            // Notify parent of success
            notifyViaPostMessage();
            notifyViaStorage(true);
            
            // Try to close popup
            setTimeout(() => window.close(), 100);
            return;
          } catch (err: any) {
            // Handle 409 Conflict - calendar already connected (success case)
            if (err?.response?.status === 409) {
              notifyViaPostMessage();
              notifyViaStorage(true);
              setTimeout(() => window.close(), 100);
              return;
            }
            
            const errorMsg = err?.response?.data?.error?.message || err.message || 'Failed to connect calendar';
            notifyViaStorage(false, errorMsg);
            setTimeout(() => window.close(), 100);
            return;
          }
        }
      }

      // Fallback: Direct navigation flow (if popup was blocked or opened directly)
      if (error) {
        setStatus('error');
        setErrorMessage(error === 'access_denied' 
          ? 'You cancelled the calendar connection' 
          : `Failed to connect calendar: ${error}`);
        return;
      }

      if (!code) {
        setStatus('error');
        setErrorMessage('No authorization code received');
        return;
      }

      try {
        const redirectUri = `${window.location.origin}/calendar/callback`;
        
        // Send code to backend to exchange for tokens
        await calendarService.connectCalendar('google', code, redirectUri);
        
        setStatus('success');
        toast.success('Calendar connected successfully!');
        
        // Redirect back to dashboard after a short delay
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1500);
      } catch (err: any) {
        // Handle 409 Conflict - calendar already connected (not an error)
        if (err?.response?.status === 409) {
          setStatus('success');
          toast.success('Calendar is already connected!');
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 1500);
          return;
        }
        
        // Log only real errors
        console.error('Error connecting calendar:', err);
        setStatus('error');
        setErrorMessage(err?.response?.data?.error?.message || err.message || 'Failed to connect calendar');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Connecting your calendar...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <XCircle className="h-12 w-12 text-destructive" />
            </div>
            <CardTitle className="text-center">Connection Failed</CardTitle>
            <CardDescription className="text-center">
              {errorMessage}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
            <p className="text-sm text-muted-foreground">Calendar connected! Redirecting...</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarCallback;
